// src/pages/business/BarcodeManager.tsx
import React, { useEffect, useState } from 'react';
import { useBusinessStore } from '@/stores/useBusinessStore';
import type { Product, BarcodeItem } from '@/stores/useBusinessStore';
import {
    QrCode,
    Search,
    Printer,
    Download,
    RefreshCw,
    CheckCircle,
    XCircle,
    Clock,
    Loader2,
    Plus,
    ScanBarcode,
    Barcode
} from 'lucide-react';

import { ScannerManagementModal } from '@/components/modals/ScannerManagementModal';
import { useNavigate } from 'react-router-dom';
import { scanCartItem } from '@/api/cart';
import { useToast } from '@/contexts/ToastContext';

const statusConfig: Record<BarcodeItem['status'], { label: string; icon: React.ReactNode; className: string }> = {
    generated: { label: 'Generated', icon: <Clock size={14} />, className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    printed: { label: 'Printed', icon: <Printer size={14} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    active: { label: 'Active', icon: <CheckCircle size={14} />, className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    deactivated: { label: 'Deactivated', icon: <XCircle size={14} />, className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

export const BarcodeManager: React.FC = () => {
    const {
        activeBusiness,
        products,
        barcodeItems,
        loading,
        fetchBusinesses,
        fetchProducts,
        fetchBarcodeItems,
        generateBarcode,
        updateBarcodeStatus,
        scanners
    } = useBusinessStore();

    const navigate = useNavigate();
    const toast = useToast();

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [generating, setGenerating] = useState(false);

    // Scanner Management
    const [isScannerManagementOpen, setIsScannerManagementOpen] = useState(false);

    // Global barcode scanner state
    const [barcodeBuffer, setBarcodeBuffer] = useState('');
    const [lastKeystrokeTime, setLastKeystrokeTime] = useState(0);
    const [recentScans, setRecentScans] = useState<Array<{
        id: string; // Unique string for array key mapping
        productId: string;
        name: string;
        barcode: string;
        scannedAt: Date;
    }>>([]);

    useEffect(() => {
        if (!activeBusiness) {
            fetchBusinesses();
        }
    }, []);

    useEffect(() => {
        if (activeBusiness) {
            fetchProducts(activeBusiness.id);
            fetchBarcodeItems(activeBusiness.id);
        }
    }, [activeBusiness]);

    // Hardware Scanner Listener Effect
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
                return;
            }

            const currentTime = new Date().getTime();

            if (e.key === 'Enter') {
                if (barcodeBuffer.length > 3) {
                    processBarcode(barcodeBuffer);
                }
                setBarcodeBuffer('');
            } else if (e.key.length === 1) {
                if (currentTime - lastKeystrokeTime > 100) {
                    setBarcodeBuffer(e.key);
                } else {
                    setBarcodeBuffer(prev => prev + e.key);
                }
                setLastKeystrokeTime(currentTime);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [barcodeBuffer, lastKeystrokeTime, products, barcodeItems]);

    const processBarcode = (scannedCode: string) => {
        let matchedProductId: string | undefined;
        let matchedProductName: string | undefined;

        const generatedMatch = barcodeItems.find((item: BarcodeItem) => item.barcode === scannedCode);
        if (generatedMatch) {
            matchedProductId = generatedMatch.product_id;
            matchedProductName = generatedMatch.product_name;
        } else {
            const productMatch = products.find((p: Product) => p.barcode === scannedCode);
            if (productMatch) {
                matchedProductId = productMatch.id;
                matchedProductName = productMatch.name;
            }
        }

        if (matchedProductId && matchedProductName) {
            const newScan = {
                id: Math.random().toString(36).substr(2, 9),
                productId: matchedProductId,
                name: matchedProductName,
                barcode: scannedCode,
                scannedAt: new Date()
            };
            setRecentScans(prev => [newScan, ...prev].slice(0, 5));

            // Automatically add to POS Cart
            if (activeBusiness) {
                scanCartItem(activeBusiness.id, scannedCode)
                    .then((res) => {
                        toast.success(`${matchedProductName} added to POS Cart`);
                    })
                    .catch((err) => {
                        console.error('Failed to add to POS cart:', err);
                        toast.error('Error', err.response?.data?.error || 'Failed to add item to POS Cart');
                    });
            }
        }
    };

    const handleGenerate = async () => {
        if (!activeBusiness || !selectedProduct) return;
        setGenerating(true);
        try {
            await generateBarcode(activeBusiness.id, selectedProduct);
            setSelectedProduct('');
        } catch (error) {
            console.error('Failed to generate barcode:', error);
        } finally {
            setGenerating(false);
        }
    };

    const handleStatusChange = async (itemId: string, status: BarcodeItem['status']) => {
        try {
            await updateBarcodeStatus(itemId, status);
        } catch (error) {
            console.error('Failed to update barcode status:', error);
        }
    };

    const handleDownload = (barcode: string) => {
        // Create a canvas element to generate barcode image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = 300;
        canvas.height = 100;

        // Draw barcode background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw barcode text
        ctx.fillStyle = '#000000';
        ctx.font = '20px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(barcode, canvas.width / 2, canvas.height / 2);

        // Add "Barcode" label
        ctx.font = '12px sans-serif';
        ctx.fillStyle = '#666666';
        ctx.fillText('Product Barcode', canvas.width / 2, canvas.height - 20);

        // Download as PNG
        const link = document.createElement('a');
        link.download = `barcode-${barcode}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const filteredBarcodes = barcodeItems.filter((item: BarcodeItem) => {
        const matchesSearch =
            (item.product_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (item.barcode || '').includes(searchTerm) ||
            (item.sku?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    if (!activeBusiness) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-primary" size={32} />
                <span className="ml-2 text-light-subtle dark:text-dark-subtle">Loading business...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl border border-light-border dark:border-dark-border shadow-sm">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                            <QrCode size={24} className="text-primary" />
                            Barcode Manager
                        </h1>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">
                            Generate, track and manage product barcodes for {activeBusiness.name}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setIsScannerManagementOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary border border-primary/20 rounded-lg text-sm hover:bg-primary/20 transition-colors"
                        >
                            <ScanBarcode size={16} />
                            Manage Scanners
                        </button>
                        <button
                            onClick={() => fetchBarcodeItems(activeBusiness.id)}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 border border-light-border dark:border-dark-border rounded-lg text-sm text-light-text dark:text-dark-text hover:bg-gray-100 dark:bg-gray-800 transition-colors disabled:opacity-50"
                        >
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                        <button
                            onClick={() => navigate(`/business/${activeBusiness.id}/cart`)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 transition-colors shadow-sm shadow-primary/30"
                        >
                            View POS Cart
                        </button>
                    </div>
                </div>

                {/* Generate barcode */}
                <div className="flex flex-col sm:flex-row gap-3 p-4 bg-gray-100 dark:bg-gray-800/30 rounded-lg border border-light-border dark:border-dark-border">
                    <select
                        value={selectedProduct}
                        onChange={(e) => setSelectedProduct(e.target.value)}
                        className="flex-1 px-4 py-2.5 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                        <option value="">Select a product to generate barcode...</option>
                        {products.map((product: Product) => (
                            <option key={product.id} value={product.id}>
                                {product.name}
                                {product.sku ? ` (SKU: ${product.sku})` : ''}
                                {product.barcode ? ' ✓' : ''}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={handleGenerate}
                        disabled={!selectedProduct || generating}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                        {generating ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Plus size={16} />
                        )}
                        Generate Barcode
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                    <input
                        type="text"
                        placeholder="Search by product, barcode or SKU..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder:text-light-subtle dark:text-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                </div>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2.5 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-[160px]"
                >
                    <option value="all">All Statuses</option>
                    <option value="generated">Generated</option>
                    <option value="printed">Printed</option>
                    <option value="active">Active</option>
                    <option value="deactivated">Deactivated</option>
                </select>
            </div>

            {/* Barcode Table */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-100 dark:bg-gray-800 border-b border-light-border dark:border-dark-border">
                            <tr>
                                <th className="text-left py-4 px-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">
                                    Product
                                </th>
                                <th className="text-left py-4 px-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">
                                    Barcode
                                </th>
                                <th className="text-left py-4 px-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider hidden md:table-cell">
                                    SKU
                                </th>
                                <th className="text-center py-4 px-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="text-left py-4 px-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider hidden lg:table-cell">
                                    Created
                                </th>
                                <th className="text-right py-4 px-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-light-border dark:divide-dark-border">
                            {filteredBarcodes.length > 0 ? (
                                filteredBarcodes.map((item: BarcodeItem) => {
                                    const status = statusConfig[item.status] || {
                                        label: item.status || 'Unknown',
                                        icon: <QrCode size={14} />,
                                        className: 'bg-gray-100 text-light-text dark:text-dark-text dark:bg-gray-800 dark:text-gray-300'
                                    };
                                    return (
                                        <tr key={item.id} className="hover:bg-gray-100 dark:bg-gray-800/30 transition-colors">
                                            <td className="py-4 px-4">
                                                <div>
                                                    <p className="font-medium text-light-text dark:text-dark-text">
                                                        {item.product_name || 'Unknown Product'}
                                                    </p>
                                                    <p className="text-xs text-light-subtle dark:text-dark-subtle mt-0.5 md:hidden">
                                                        SKU: {item.sku || '—'}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <code className="text-sm bg-gray-100 dark:bg-gray-800 px-2 py-1.5 rounded-md font-mono text-light-text dark:text-dark-text border border-light-border dark:border-dark-border">
                                                    {item.barcode}
                                                </code>
                                            </td>
                                            <td className="py-4 px-4 text-light-subtle dark:text-dark-subtle hidden md:table-cell">
                                                {item.sku || '—'}
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full ${status.className}`}>
                                                    {status.icon}
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4 text-sm text-light-subtle dark:text-dark-subtle hidden lg:table-cell">
                                                {new Date(item.created_at).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </td>
                                            <td className="py-4 px-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <select
                                                        value={item.status}
                                                        onChange={(e) => handleStatusChange(item.id, e.target.value as BarcodeItem['status'])}
                                                        className="text-xs px-2 py-1.5 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-md text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                                        aria-label="Change status"
                                                    >
                                                        <option value="generated">Generated</option>
                                                        <option value="printed">Printed</option>
                                                        <option value="active">Active</option>
                                                        <option value="deactivated">Deactivated</option>
                                                    </select>
                                                    <button
                                                        onClick={() => handleStatusChange(item.id, 'printed')}
                                                        className="p-2 rounded-lg text-light-subtle dark:text-dark-subtle hover:text-primary hover:bg-primary/10 transition-colors"
                                                        title="Mark as Printed"
                                                    >
                                                        <Printer size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownload(item.barcode)}
                                                        className="p-2 rounded-lg text-light-subtle dark:text-dark-subtle hover:text-primary hover:bg-primary/10 transition-colors"
                                                        title="Download Barcode"
                                                    >
                                                        <Download size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center text-light-subtle dark:text-dark-subtle">
                                        <QrCode size={48} className="mx-auto mb-4 opacity-30" />
                                        <p className="text-lg font-medium mb-1">No barcodes found</p>
                                        <p className="text-sm">
                                            {searchTerm || filterStatus !== 'all'
                                                ? 'Try adjusting your search or filter'
                                                : 'Generate your first barcode above'}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Status Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {(['generated', 'printed', 'active', 'deactivated'] as const).map((status) => {
                    const config = statusConfig[status];
                    const count = barcodeItems.filter((item: BarcodeItem) => item.status === status).length;
                    return (
                        <div
                            key={status}
                            className="bg-light-card dark:bg-dark-card p-5 rounded-xl border border-light-border dark:border-dark-border text-center hover:shadow-md transition-shadow"
                        >
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full mb-3 ${config.className}`}>
                                {config.icon}
                                {config.label}
                            </div>
                            <p className="text-3xl font-bold text-light-text dark:text-dark-text">{count}</p>
                            <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">Total {config.label}</p>
                        </div>
                    );
                })}
            </div>

            {/* Recent Scans Feed */}
            {recentScans.length > 0 && (
                <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border overflow-hidden shadow-sm">
                    <div className="p-4 border-b border-light-border dark:border-dark-border flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
                        <h3 className="font-semibold text-light-text dark:text-dark-text flex items-center gap-2">
                            <ScanBarcode size={18} className="text-blue-500" /> Recent Scans
                        </h3>
                        {scanners.find(s => s.status === 'active') && (
                            <span className="bg-green-500/10 text-green-600 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                                Scanner Active
                            </span>
                        )}
                    </div>
                    <div className="divide-y divide-light-border dark:divide-dark-border">
                        {recentScans.map(scan => (
                            <div
                                key={scan.id}
                                onClick={() => navigate(`/business/products/${scan.productId}`)}
                                className="p-4 flex justify-between items-center hover:bg-gray-100 dark:bg-gray-800 transition-colors cursor-pointer"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-600">
                                        <Barcode size={20} />
                                    </div>
                                    <div>
                                        <p className="font-medium text-light-text dark:text-dark-text">{scan.name}</p>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle flex items-center gap-1 mt-0.5">
                                            <Clock size={12} /> {scan.scannedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-lg text-light-text dark:text-dark-text border border-light-border dark:border-dark-border">
                                        {scan.barcode}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Modals */}
            <ScannerManagementModal
                isOpen={isScannerManagementOpen}
                onClose={() => setIsScannerManagementOpen(false)}
            />
        </div>
    );
};